/**
 * Background Preloader Service
 * 
 * Preloads frequently accessed data during browser idle time to improve performance
 * for both admin dashboard and SO search functionality.
 * 
 * Strategy:
 * - Uses requestIdleCallback to avoid blocking main app
 * - Preloads ±7 days of sessions (14-day window) 
 * - Caches staff data (expensive 2+ second fetch)
 * - Stores in sessionStorage for instant access
 * - Benefits both admin dashboard and SO search performance
 */

import { operationsBase, TABLES } from './airtable.js';

// Cache keys
export const PRELOAD_CACHE_KEYS = {
  SESSIONS: 'preload_sessions_cache',
  STAFF: 'admin_staff_cache', // Reuse existing staff cache key
  SCHEDULED_OFFERINGS: 'preload_so_cache',
  SO_SEARCH: 'preload_so_search_cache' // New: For SO search optimization
};

// Cache TTL (30 minutes for preloaded data)
const PRELOAD_CACHE_TTL = 30 * 60 * 1000;

/**
 * Check if browser supports idle callbacks and if we should preload
 */
const shouldPreload = () => {
  return (
    typeof window !== 'undefined' &&
    'requestIdleCallback' in window &&
    navigator.connection?.effectiveType !== 'slow-2g' // Skip on very slow connections
  );
};

/**
 * Get date range for ±7 days from today
 */
const getPreloadDateRange = () => {
  const today = new Date();
  
  // 7 days ago
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  sevenDaysAgo.setHours(0, 0, 0, 0);
  
  // 7 days from now
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(today.getDate() + 7);
  sevenDaysFromNow.setHours(23, 59, 59, 999);
  
  // Format for Airtable
  const formatDate = (date) => date.toISOString().split('T')[0];
  
  return {
    start: formatDate(sevenDaysAgo),
    end: formatDate(sevenDaysFromNow)
  };
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cacheKey) => {
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return false;
    
    const { timestamp } = JSON.parse(cached);
    return Date.now() - timestamp < PRELOAD_CACHE_TTL;
  } catch {
    return false;
  }
};

/**
 * Preload sessions data for ±7 days.
 * @returns {Promise<Array>} Resolves with an array of session records (raw Airtable objects).
 * @throws {Error} Propagates Airtable API errors to the caller for outcome tracking.
 */
const preloadSessions = async () => {
  // Return cached records if still valid (avoids redundant fetches on repeated calls)
  if (isCacheValid(PRELOAD_CACHE_KEYS.SESSIONS)) {
    console.log('💾 Background: Sessions already cached, skipping preload');
    const cached = JSON.parse(sessionStorage.getItem(PRELOAD_CACHE_KEYS.SESSIONS));
    return cached.data || [];
  }

  console.log('🔄 Background: Preloading sessions (±7 days)...');

  const { start, end } = getPreloadDateRange();

  console.time('background-sessions-preload');

  const sessionRecords = await operationsBase(TABLES.SESSIONS)
    .select({
      filterByFormula: `AND(
        IS_AFTER({Start}, "${start}"),
        IS_BEFORE({Start}, "${end}")
      )`,
      sort: [{ field: 'Start', direction: 'desc' }],
      maxRecords: 100, // Reasonable limit for 14-day window
      fields: [
        'Session Name', 'Start', 'SOID', 'SO (from SOID)',
        'ProgramName (from SOID)', 'PL', 'PL Override',
        'Session Status', 'Survey Link TRUTH'
      ]
    })
    .all();

  console.timeEnd('background-sessions-preload');

  // Cache the sessions
  const cacheData = {
    timestamp: Date.now(),
    dateRange: { start, end },
    data: sessionRecords.map(record => ({
      id: record.id,
      fields: record.fields
    }))
  };

  sessionStorage.setItem(PRELOAD_CACHE_KEYS.SESSIONS, JSON.stringify(cacheData));
  console.log(`✅ Background: Cached ${sessionRecords.length} sessions for ±7 days`);

  return sessionRecords;
};

/**
 * Preload staff data (reuses existing cache system).
 * @returns {Promise<void>} Resolves when staff data is cached. May reject on API errors.
 * @throws {Error} Propagates Airtable API errors to the caller for outcome tracking.
 */
const preloadStaff = async () => {
  // Check if staff data is already cached
  if (isCacheValid(PRELOAD_CACHE_KEYS.STAFF)) {
    console.log('💾 Background: Staff data already cached, skipping preload');
    return;
  }

  console.log('🔄 Background: Preloading staff data...');

  console.time('background-staff-preload');

  const staffRecords = await operationsBase('Staff')
    .select({
      fields: ['Name', 'Color'],
      filterByFormula: `{Status} = "Active"`
    })
    .all();

  console.timeEnd('background-staff-preload');

  // Cache using the same format as the main staff cache
  const serializedData = staffRecords.map(record => ({
    id: record.id,
    fields: {
      'Name': record.get('Name'),
      'Color': record.get('Color')
    }
  }));

  const cacheData = {
    timestamp: Date.now(),
    data: serializedData
  };

  sessionStorage.setItem(PRELOAD_CACHE_KEYS.STAFF, JSON.stringify(cacheData));
  console.log(`✅ Background: Cached ${staffRecords.length} staff records`);
};

/**
 * Preload recent scheduled offerings that match the session date range.
 * @param {Array<Object>} sessionRecords - Session record objects used to extract SOIDs for preloading.
 * @returns {Promise<void>} Resolves when SO data is cached.
 * @throws {Error} Propagates errors for outcome tracking by runBackgroundPreload.
 */
const preloadRecentScheduledOfferings = async (sessionRecords) => {
  if (!sessionRecords || sessionRecords.length === 0) return;

  console.log('🔄 Background: Preloading recent scheduled offerings...');

  // Extract unique SOIDs from sessions
  const uniqueSOIDs = [...new Set(
    sessionRecords
      .map(record => record.fields?.SOID)
      .filter(Boolean)
  )];

  if (uniqueSOIDs.length === 0) return;

  console.time('background-so-preload');

  // Build formula for multiple SOIDs
  const soFormula = uniqueSOIDs.length === 1
    ? `{SOID} = "${uniqueSOIDs[0]}"`
    : `OR(${uniqueSOIDs.map(soid => `{SOID} = "${soid}"`).join(',')})`;

  const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
    .select({
      filterByFormula: soFormula,
      fields: ['SO', 'SOID', 'Client Name', 'Type', 'SO Status']
    })
    .all();

  console.timeEnd('background-so-preload');

  // Cache the scheduled offerings
  const cacheData = {
    timestamp: Date.now(),
    data: soRecords.map(record => ({
      id: record.id,
      fields: record.fields
    }))
  };

  sessionStorage.setItem(PRELOAD_CACHE_KEYS.SCHEDULED_OFFERINGS, JSON.stringify(cacheData));
  console.log(`✅ Background: Cached ${soRecords.length} scheduled offerings`);
};

/**
 * Preload SOs for search optimization - broader scope than session-related SOs.
 * Caches SOs that have sessions within ±7 days for faster SO search.
 * @param {Array<Object>} sessionRecords - Session record objects used to extract SO numbers for preloading.
 * @returns {Promise<Array>} Resolves with cached SO records.
 * @throws {Error} Propagates errors for outcome tracking by runBackgroundPreload.
 */
const preloadSOsForSearch = async (sessionRecords) => {
  if (!sessionRecords || sessionRecords.length === 0) return [];

  console.log('🔄 Background: Preloading SOs for search optimization...');

  // Extract unique SO numbers from sessions (broader than just SOIDs)
  const uniqueSONumbers = [...new Set(
    sessionRecords
      .map(record => {
        const soNumber = record.fields?.['SO (from SOID)'];
        return Array.isArray(soNumber) ? soNumber[0] : soNumber;
      })
      .filter(Boolean)
  )];

  if (uniqueSONumbers.length === 0) return [];

  console.time('background-so-search-preload');

  // Build formula for SO numbers (what users actually search for)
  const soSearchFormula = uniqueSONumbers.length === 1
    ? `{SO} = "${uniqueSONumbers[0]}"`
    : `OR(${uniqueSONumbers.map(so => `{SO} = "${so}"`).join(',')})`;

  // Fetch comprehensive SO data for search (more fields than admin needs)
  const soSearchRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
    .select({
      filterByFormula: soSearchFormula,
      fields: [
        'SO', 'SOID', 'Client', 'Client Name', 'Participating Clients',
        'Type', 'SO Status', 'Delivery Start', 'Delivery End',
        'Program', 'ProgramName', 'PL', 'PL Name', 'Virtual Platform',
        'PL Notes', 'Lang'
      ]
    })
    .all();

  console.timeEnd('background-so-search-preload');

  // Cache the SO search data
  const { start, end } = getPreloadDateRange();
  const cacheData = {
    timestamp: Date.now(),
    dateRange: { start, end },
    data: soSearchRecords.map(record => ({
      id: record.id,
      fields: record.fields
    }))
  };

  sessionStorage.setItem(PRELOAD_CACHE_KEYS.SO_SEARCH, JSON.stringify(cacheData));
  console.log(`✅ Background: Cached ${soSearchRecords.length} SOs for search (${start} to ${end})`);

  return soSearchRecords;
};

/**
 * Main preloader function - coordinates all background loading.
 * Staff and sessions start concurrently; SO preloads run after sessions resolve.
 * @returns {Promise<void>} Resolves when all background preload phases complete.
 */
const runBackgroundPreload = async () => {
  if (!shouldPreload()) {
    console.log('⏭️ Background preload skipped (browser limitations or slow connection)');
    return;
  }

  console.log('🚀 Background preload starting...');
  const startTime = performance.now();
  let outcome = 'success';

  try {
    // Start both independently — staff runs in background, we only await sessions
    // so SO preloads aren't blocked by a slow staff fetch (10-14s cold start).
    const staffPromise = preloadStaff();
    const sessionsPromise = preloadSessions();

    // Await sessions first — SO preloads depend on session data
    let sessionRecords = null;
    try {
      sessionRecords = await sessionsPromise;
    } catch (err) {
      outcome = 'partial_failure';
      console.warn('Background session preload failed:', err);
    }

    // Phase 2: SO preloads depend on session data — run them in parallel with each other.
    // SO failures are non-fatal but logged for visibility.
    if (sessionRecords && sessionRecords.length > 0) {
      const soResults = await Promise.allSettled([
        preloadRecentScheduledOfferings(sessionRecords),
        preloadSOsForSearch(sessionRecords)
      ]);
      const soNames = ['preloadRecentScheduledOfferings', 'preloadSOsForSearch'];
      soResults.forEach((result, i) => {
        if (result.status === 'rejected') {
          console.warn(`Background ${soNames[i]} failed:`, result.reason);
          if (outcome === 'success') outcome = 'partial_failure';
        }
      });
    } else if (outcome !== 'partial_failure') {
      console.log('⏭️ Background: Skipping SO preloads (no session records available)');
    }

    // Now wait for staff to finish (don't leave it dangling)
    try {
      await staffPromise;
    } catch (err) {
      outcome = outcome === 'partial_failure' ? 'error' : 'partial_failure';
      console.warn('Background staff preload failed:', err);
    }
  } catch (err) {
    outcome = 'error';
    console.error('Background preload encountered an unexpected error:', err);
  } finally {
    const totalTime = performance.now() - startTime;
    console.log(`✅ Background preload completed in ${totalTime.toFixed(0)}ms`);

    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'background_preload_complete', {
        event_category: 'performance',
        value: Math.round(totalTime),
        custom_parameter_1: outcome
      });
    }
  }
};

/**
 * Start background preloading with idle callback
 */
export const startBackgroundPreload = () => {
  if (!shouldPreload()) return;
  
  // Wait for main app to settle, then start preloading during idle time
  setTimeout(() => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        runBackgroundPreload,
        { timeout: 5000 } // Fallback if browser stays busy
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(runBackgroundPreload, 2000);
    }
  }, 1000); // Wait 1 second after app load
};

/**
 * Get preloaded sessions from cache
 */
export const getPreloadedSessions = () => {
  try {
    // Skip if running in Node.js environment (e.g., import scripts)
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return null;
    }
    
    const cached = sessionStorage.getItem(PRELOAD_CACHE_KEYS.SESSIONS);
    if (!cached) return null;
    
    const { timestamp, data, dateRange } = JSON.parse(cached);
    
    // Check if cache is still valid
    if (Date.now() - timestamp > PRELOAD_CACHE_TTL) {
      sessionStorage.removeItem(PRELOAD_CACHE_KEYS.SESSIONS);
      return null;
    }
    
    console.log(`💾 Using preloaded sessions cache (${data.length} records, ${dateRange.start} to ${dateRange.end})`);
    return data;
  } catch (error) {
    console.warn('Error reading preloaded sessions cache:', error);
    return null;
  }
};

/**
 * Get preloaded scheduled offerings from cache
 */
export const getPreloadedScheduledOfferings = () => {
  try {
    // Skip if running in Node.js environment (e.g., import scripts)
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return null;
    }
    
    const cached = sessionStorage.getItem(PRELOAD_CACHE_KEYS.SCHEDULED_OFFERINGS);
    if (!cached) return null;
    
    const { timestamp, data } = JSON.parse(cached);
    
    // Check if cache is still valid
    if (Date.now() - timestamp > PRELOAD_CACHE_TTL) {
      sessionStorage.removeItem(PRELOAD_CACHE_KEYS.SCHEDULED_OFFERINGS);
      return null;
    }
    
    console.log(`💾 Using preloaded scheduled offerings cache (${data.length} records)`);
    return data;
  } catch (error) {
    console.warn('Error reading preloaded SO cache:', error);
    return null;
  }
};

/**
 * Get preloaded SOs for search from cache
 */
export const getPreloadedSOsForSearch = (soNumber) => {
  try {
    // Skip if running in Node.js environment (e.g., import scripts)
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return null;
    }
    
    const cached = sessionStorage.getItem(PRELOAD_CACHE_KEYS.SO_SEARCH);
    if (!cached) return null;
    
    const { timestamp, data, dateRange } = JSON.parse(cached);
    
    // Check if cache is still valid
    if (Date.now() - timestamp > PRELOAD_CACHE_TTL) {
      sessionStorage.removeItem(PRELOAD_CACHE_KEYS.SO_SEARCH);
      return null;
    }
    
    // If specific SO requested, filter for it
    if (soNumber) {
      const matchingSO = data.find(so => so.fields?.SO === soNumber);
      if (matchingSO) {
        console.log(`💾 Found SO ${soNumber} in preloaded search cache`);
        return [matchingSO];
      }
      return null;
    }
    
    // Return all cached SOs
    console.log(`💾 Using preloaded SO search cache (${data.length} records, ${dateRange.start} to ${dateRange.end})`);
    return data;
  } catch (error) {
    console.warn('Error reading preloaded SO search cache:', error);
    return null;
  }
};

// Export for debugging/monitoring
export const getPreloadCacheStatus = () => {
  return {
    sessions: isCacheValid(PRELOAD_CACHE_KEYS.SESSIONS),
    staff: isCacheValid(PRELOAD_CACHE_KEYS.STAFF),
    scheduledOfferings: isCacheValid(PRELOAD_CACHE_KEYS.SCHEDULED_OFFERINGS),
    soSearch: isCacheValid(PRELOAD_CACHE_KEYS.SO_SEARCH)
  };
};